import type { MarketedMembershipPlan } from '@frontend/api-client';
import { getMembershipPlanDisplayPricing } from '@frontend/api-client';
import type { PromotionDetails } from '@frontend/api-types';
import { dateFormatters, formatCurrencyNatural } from '@frontend/duck-tape';
import { Text, XStack, YStack } from '@frontend/web-react';
import type { ReactNode } from 'react';

export type SubscriptionSummaryProps = {
  nextBillingDate: DateISOString;
  promoCode?: string;
  promotionDescription?: string;
  promotionDetails?: PromotionDetails;
  promotionName?: string;
  selectedPlanData: MarketedMembershipPlan;
};

const formatEstimatedTasks = (min: number, max: number): string => `~ ${min} - ${max} tasks`;

const SubscriptionSummaryDetails = ({ children, label }: { children: ReactNode; label: string }) => {
  return (
    <YStack gapY="xs">
      <Text color="tertiary" type="p-sm">
        {label}
      </Text>
      <XStack borderColor="borderTertiary" borderWidth={1} justifyContent="between" p="md" radius="lg">
        {children}
      </XStack>
    </YStack>
  );
};

export const SubscriptionSummary = ({
  nextBillingDate,
  promoCode,
  promotionDescription,
  promotionDetails,
  promotionName,
  selectedPlanData,
}: SubscriptionSummaryProps) => {
  const displayPricing = getMembershipPlanDisplayPricing(selectedPlanData);
  const hasPromo = Boolean(displayPricing?.secondaryPrice && promotionDetails && promotionName);

  const estimatedTasks = formatEstimatedTasks(
    selectedPlanData.estimatedTasksPerMonth.min,
    selectedPlanData.estimatedTasksPerMonth.max,
  );

  return (
    <YStack gapY="lg">
      {hasPromo && promoCode ? (
        <SubscriptionSummaryDetails label="Promotion">
          <YStack className="md:flex-row md:justify-between w-full" gapX="sm" gapY="xs">
            <Text type="h5">{promoCode}</Text>
            <Text type="p-md">{promotionDescription}</Text>
          </YStack>
        </SubscriptionSummaryDetails>
      ) : null}

      <YStack gapY="md">
        <YStack gapY="xs">
          <SubscriptionSummaryDetails label="Subscription">
            <YStack>
              <Text type="h5">{selectedPlanData.label}</Text>
              <Text color="tertiary" type="p-md">
                {estimatedTasks}
              </Text>
            </YStack>
            <YStack alignItems="end" gapY="xs">
              <Text type="h5">{formatCurrencyNatural(displayPricing?.primaryPrice ?? selectedPlanData.price)}/mo</Text>
              {displayPricing?.secondaryPrice ? (
                <Text className="line-through" color="tertiary" type="p-sm">
                  {formatCurrencyNatural(displayPricing.secondaryPrice)}/mo
                </Text>
              ) : null}
            </YStack>
          </SubscriptionSummaryDetails>
          <Text color="tertiary" type="p-sm">
            Next billing date: {dateFormatters['LL'](nextBillingDate)}
          </Text>
        </YStack>
      </YStack>
    </YStack>
  );
};
